// Pastel color utilities — HSL-based, saturation and lightness are fixed to the pastel range.

const PASTEL_SATURATION = 65; // %
const PASTEL_LIGHTNESS = 78; // %
const FULL_HUE_RANGE = 360;

/**
 * Picks a new pastel color that is as different as possible from all the given colors.
 *
 * Algorithm: Farthest Point Sampling (also called Greedy Maximin) on the hue circle
 *
 * Steps:
 *
 * 1. Take all the existing colors (in hex format) and convert them to their hue value (0–359 degrees on the color wheel).
 *    - We store these hues in a set called usedHues, so we can quickly check if a hue is already taken.
 *
 * 2. If there are no used hues (no colors yet), just pick a random pastel color.
 *
 * 3. If every possible hue is already used (all 360), pick a new hue by adding the golden angle (137.508°) to the seed color (or a random hue if no seed is given).
 *    - This is a mathematical trick to spread colors out as much as possible.
 *
 * 4. Otherwise, for every possible hue (0–359):
 *    a. If this hue is already used, skip it.
 *    b. For each unused hue, calculate how close it is to the nearest used hue (using circular distance on the color wheel).
 *    c. Keep track of the hue(s) that are farthest away from any used hue (the biggest minimum distance).
 *
 * 5. If there are several equally good "farthest" hues, pick one random.
 *
 * 6. Return the pastel color for the chosen hue.
 *
 * This ensures that every new color is as visually different as possible from all the previous ones.
 */
export function getMostDistinctPastelColorHex(existingHexColors: string[], seedHex?: string): string {
    // Step 1
    const usedHues = new Set<number>();
    for (const color of existingHexColors) {
        if (isHexColor(color)) {
            usedHues.add(normalizeHue(hexToPastelHue(color)));
        }
    }

    // Step 2
    if (usedHues.size === 0) {
        return pastelHueToHex(Math.floor(Math.random() * FULL_HUE_RANGE));
    }

    // Step 3
    if (usedHues.size >= FULL_HUE_RANGE) {
        const seedHue = isHexColor(seedHex) ? hexToPastelHue(seedHex) : Math.floor(Math.random() * FULL_HUE_RANGE);
        return pastelHueToHex((seedHue + 137.508) % FULL_HUE_RANGE);
    }

    // Step 4
    let bestDistance = -1;
    let bestHues: number[] = [];
    for (let candidate = 0; candidate < FULL_HUE_RANGE; candidate++) {
        if (usedHues.has(candidate)) {
            continue; // Skip hues that are already used
        }

        // For this candidate hue, find the smallest distance to any used hue
        let minDistance = FULL_HUE_RANGE;
        for (const usedHue of usedHues) {
            const distance = circularHueDistance(candidate, usedHue);
            if (distance < minDistance) {
                minDistance = distance;
            }
            if (minDistance < bestDistance) {
                break;
            }
        }

        if (minDistance > bestDistance) {
            bestDistance = minDistance;
            bestHues = [candidate];
            continue;
        }
        
        if (minDistance === bestDistance) {
            bestHues.push(candidate);
        }
    }

    // Step 5
    if (bestHues.length === 0) {
        return pastelHueToHex(Math.floor(Math.random() * FULL_HUE_RANGE));
    }

    // Step 6
    const pickIndex = Math.floor(Math.random() * bestHues.length);
    return pastelHueToHex(bestHues[pickIndex]);
}

function normalizeHue(hue: number): number {
    const normalized = Math.round(hue) % FULL_HUE_RANGE;
    return normalized < 0 ? normalized + FULL_HUE_RANGE : normalized;
}

function circularHueDistance(a: number, b: number): number {
    const d = Math.abs(a - b) % FULL_HUE_RANGE;
    return d > FULL_HUE_RANGE / 2 ? FULL_HUE_RANGE - d : d;
}

function isHexColor(hex?: string): hex is string {
    return typeof hex === 'string' && /^#[0-9a-fA-F]{6}$/.test(hex);
}

/** Convert HSL to sRGB hex string "#rrggbb". h: 0–360, s: 0–100, l: 0–100. */
function hslToHex(h: number, s: number, l: number): string {
    s /= 100;
    l /= 100;
    const k = (n: number) => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    const r = Math.round(f(0) * 255);
    const g = Math.round(f(8) * 255);
    const b = Math.round(f(4) * 255);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/** Extract HSL channels from an sRGB hex string. Returns [h: 0–360, s: 0–100, l: 0–100]. */
export function hexToHsl(hex: string): [number, number, number] {
    const clean = hex.replace('#', '');
    const r = parseInt(clean.slice(0, 2), 16) / 255;
    const g = parseInt(clean.slice(2, 4), 16) / 255;
    const b = parseInt(clean.slice(4, 6), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    if (max === min) {
        return [0, 0, l * 100];
    }
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h;
    if (max === r) {
        h = (g - b) / d + (g < b ? 6 : 0);
    } else if (max === g) {
        h = (b - r) / d + 2;
    } else {
        h = (r - g) / d + 4;
    }
    return [h * 60, s * 100, l * 100];
}

/** Convert a hue angle (0–360) to a pastel hex color at fixed saturation and lightness. */
export function pastelHueToHex(hue: number): string {
    return hslToHex(hue, PASTEL_SATURATION, PASTEL_LIGHTNESS);
}

/** Extract the hue angle (0–360) from any hex color, for initialising the pastel hue slider. */
export function hexToPastelHue(hex: string): number {
    const [h] = hexToHsl(hex);
    return h;
}

// Returns a random hex color string (e.g. #a1b2c3)
export function getRandomColorHex(): string {
    // Avoid too-bright/too-dark colors
    const min = 60;
    const max = 200;
    const r = Math.floor(Math.random() * (max - min) + min);
    const g = Math.floor(Math.random() * (max - min) + min);
    const b = Math.floor(Math.random() * (max - min) + min);
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}
